/**
 * WorkspacesNav component
 * Renders the workspaces navigation section in the sidebar with:
 * - WORKSPACES section header with home icon linking to dashboard
 * - Workspace items with two-letter abbreviation in colored circle
 * - Selected state with left border accent in workspace color
 * - Refined typography and spacing ("Command Docket" aesthetic)
 */

import { Link, useLocation } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Tooltip,
  shorthands,
} from '@fluentui/react-components';
import { Home20Regular } from '@fluentui/react-icons';
import type { WorkspaceNavItem } from './types';
import { getWorkspaceAbbreviation } from '../../utils/workspaceColors';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '20px',
    paddingRight: '20px',
    marginBottom: '4px',
    height: '32px', // Fixed height to prevent any shift when collapsed
    boxSizing: 'border-box',
  },
  sectionHeaderRowCollapsed: {
    justifyContent: 'center',
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  sectionHeader: {
    // Small caps typography - refined and editorial
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    fontVariant: 'small-caps',
  },
  sectionHeaderCollapsed: {
    // Subtle dot separator when collapsed
    width: '4px',
    height: '4px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorNeutralStroke1,
  },
  dashboardItem: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    ...shorthands.padding('6px', '10px'),
    color: tokens.colorNeutralForeground2,
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    ...shorthands.borderRadius('8px'),
    ...shorthands.borderLeft('3px', 'solid', 'transparent'),
    marginLeft: '-3px',
    marginBottom: '4px',
    transitionProperty: 'all',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
  },
  dashboardItemCollapsed: {
    marginLeft: 0,
    ...shorthands.borderLeft('none'),
    ...shorthands.padding('6px'),
    justifyContent: 'center',
  },
  dashboardItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandForeground1,
    color: tokens.colorBrandForeground1,
    fontWeight: '600',
  },
  dashboardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    ...shorthands.borderRadius('8px'),
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  dashboardIconSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  dashboardLabel: {
    fontSize: '14px',
  },
  dashboardLabelCollapsed: {
    display: 'none',
  },
  workspaceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    ...shorthands.padding('0', '12px'),
  },
  workspaceListCollapsed: {
    ...shorthands.padding('0', '8px'),
    alignItems: 'center',
  },
  workspaceItem: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    ...shorthands.padding('6px', '10px'),
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    ...shorthands.borderRadius('8px'),
    // Left border accent - hidden by default
    ...shorthands.borderLeft('3px', 'solid', 'transparent'),
    marginLeft: '-3px', // Compensate for border width
    transitionProperty: 'all',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
  },
  workspaceItemCollapsed: {
    marginLeft: 0,
    ...shorthands.borderLeft('none'),
    ...shorthands.padding('6px'),
    justifyContent: 'center',
  },
  workspaceItemSelected: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
    // Border color set dynamically via style prop
  },
  workspaceAvatar: {
    width: '32px',
    height: '32px',
    ...shorthands.borderRadius('8px'), // Rounded square instead of circle
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.02em',
    boxShadow: tokens.shadow4,
  },
  workspaceLabel: {
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground1,
  },
  workspaceLabelSelected: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  workspaceLabelCollapsed: {
    display: 'none',
  },
});

interface WorkspacesNavProps {
  workspaces: WorkspaceNavItem[];
  collapsed?: boolean;
}

export function WorkspacesNav({ workspaces, collapsed = false }: WorkspacesNavProps) {
  const styles = useStyles();
  const location = useLocation();
  const currentPath = location.pathname;

  // Sort workspaces alphabetically by label
  const sortedWorkspaces = [...workspaces].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  // Check if we're on the workspaces dashboard (exact match)
  const isOnDashboard = currentPath === '/app/workspaces';

  // Dashboard/All Workspaces item
  const dashboardContent = (
    <Link
      to="/app/workspaces"
      className={mergeClasses(
        styles.dashboardItem,
        isOnDashboard && styles.dashboardItemSelected,
        collapsed && styles.dashboardItemCollapsed
      )}
      data-testid="workspaces-home-link"
      data-selected={isOnDashboard ? 'true' : undefined}
    >
      <span className={mergeClasses(
        styles.dashboardIcon,
        isOnDashboard && styles.dashboardIconSelected
      )}>
        <Home20Regular />
      </span>
      <span className={mergeClasses(
        styles.dashboardLabel,
        collapsed && styles.dashboardLabelCollapsed
      )}>
        Dashboard
      </span>
    </Link>
  );

  return (
    <div className={styles.section}>
      <div className={mergeClasses(
        styles.sectionHeaderRow,
        collapsed && styles.sectionHeaderRowCollapsed
      )}>
        <span className={collapsed ? styles.sectionHeaderCollapsed : styles.sectionHeader}>
          {!collapsed && 'MATTERS'}
        </span>
      </div>

      <div className={mergeClasses(
        styles.workspaceList,
        collapsed && styles.workspaceListCollapsed
      )}>
        {/* Dashboard as first item */}
        {collapsed ? (
          <Tooltip content="Dashboard" relationship="label" positioning="after">
            {dashboardContent}
          </Tooltip>
        ) : (
          dashboardContent
        )}
        {sortedWorkspaces.map((workspace) => {
          const isSelected = currentPath === `/app/${workspace.route}` || currentPath.startsWith(`/app/${workspace.route}/`);
          const abbreviation = getWorkspaceAbbreviation(workspace.label);

          // Dynamic styles for selected state - left border accent in workspace color (only when expanded)
          const itemStyle: React.CSSProperties = isSelected && !collapsed
            ? { borderLeftColor: workspace.color }
            : {};

          const itemContent = (
            <Link
              key={workspace.id}
              to={`/app/${workspace.route}`}
              className={mergeClasses(
                styles.workspaceItem,
                isSelected && styles.workspaceItemSelected,
                collapsed && styles.workspaceItemCollapsed
              )}
              style={itemStyle}
              data-testid={`workspace-item-${workspace.id}`}
              data-selected={isSelected ? 'true' : undefined}
            >
              <span
                className={styles.workspaceAvatar}
                style={{ backgroundColor: workspace.color }}
                data-testid={`workspace-avatar-${workspace.id}`}
              >
                {abbreviation}
              </span>
              <span className={mergeClasses(
                styles.workspaceLabel,
                isSelected && styles.workspaceLabelSelected,
                collapsed && styles.workspaceLabelCollapsed
              )}>
                {workspace.label}
              </span>
            </Link>
          );

          // Wrap in tooltip when collapsed
          if (collapsed) {
            return (
              <Tooltip key={workspace.id} content={workspace.label} relationship="label" positioning="after">
                {itemContent}
              </Tooltip>
            );
          }

          return itemContent;
        })}
      </div>
    </div>
  );
}
